// re.rejouis.fr — shell de l'app : routage + état + panier + connexion client.
// Étape « connexion » : login Shopify Customer Account API (OAuth) câblé.
import { useEffect, useMemo, useRef, useState } from 'react';
import Accueil from './screens/Accueil.jsx';
import Catalogue from './screens/Catalogue.jsx';
import Compte from './screens/Compte.jsx';
import Scan from './screens/Scan.jsx';
import Etat from './screens/Etat.jsx';
import Panier from './screens/Panier.jsx';
import Verifications from './screens/Verifications.jsx';
import Soumission from './screens/Soumission.jsx';
import Confirmation from './screens/Confirmation.jsx';
import Don from './screens/Don.jsx';
import Lot from './screens/Lot.jsx';
import Reference from './screens/Reference.jsx';
import PrixReprise from './screens/PrixReprise.jsx';
import Reprendre from './screens/Reprendre.jsx';
import MesDemandes from './screens/MesDemandes.jsx';
import Retour from './screens/Retour.jsx';
import Recherches from './screens/Recherches.jsx';
import { Spinner, LogoRe, Icons, LARGEUR_MAX } from './components/ui.jsx';
import AvisPopup from './components/AvisPopup.jsx';
import { handleCallback, clientConnecte, login, logout, authConfigure } from './lib/auth.js';
import { invaliderDemandes } from './lib/api.js';
import { REFERENCEMENT_ACTIF, MODE_APERCU } from './lib/flags.js';
import posthog, { suivre, mesureConfiguree, mesureRefusee, basculerMesure } from './lib/posthog.js';

// Écrans transitoires : on ne les persiste pas (un reload retombe sur l'accueil
// plutôt que de re-soumettre ou de figer un écran d'attente).
const ECRANS_TRANSITOIRES = new Set(['callback', 'reprendre', 'soumission', 'confirmation']);
const POST_LOGIN_KEY = 'reprise_post_login';

const STORE_KEY = 'reprise_v1';

const nouvelId = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

function chargeEtat() {
  try { return JSON.parse(localStorage.getItem(STORE_KEY)) || {}; } catch { return {}; }
}

// Lien re.rejouis.fr/#panier (heatmap PostHog, lien partagé) : ouvre cet écran.
// Seulement les écrans qui s'affichent sans paramètre.
const ECRANS_PAR_HASH = new Set(['accueil', 'catalogue', 'scan', 'lot', 'panier', 'verifications', 'compte', 'mes-demandes', 'retour', 'prix-reprise', 'don', 'recherches', 'offres']);
function ecranDuHash() {
  const h = window.location.hash.replace('#', '');
  return ECRANS_PAR_HASH.has(h) ? h : null;
}

// Écran d'attente pendant le retour OAuth (/callback).
function CallbackEcran({ erreur, onRetour }) {
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, textAlign: 'center' }}>
      {erreur ? (
        <>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Connexion impossible</div>
          <div style={{ fontSize: 13, color: 'var(--stone)', maxWidth: 320, lineHeight: 1.5 }}>{erreur}</div>
          <button className="btn btn-secondary" onClick={onRetour}>Retour</button>
        </>
      ) : (
        <Spinner size={22} label="Connexion à ton compte Rejouis…" />
      )}
    </div>
  );
}

export default function App() {
  const sauve = useMemo(chargeEtat, []);
  const estCallback = window.location.pathname === '/callback';
  const [ecran, setEcran] = useState(estCallback ? 'callback' : (ecranDuHash() || sauve.ecran || 'accueil'));
  const [params, setParams] = useState(sauve.params || null);
  // Chaque ligne a son identifiant : un même modèle peut être ajouté plusieurs fois, avec
  // un paiement choisi pour chacun (bon d'achat ou virement). Paniers sauvés avant : id ajouté.
  const [panier, setPanier] = useState(() => (sauve.panier || []).map((l) => (l.ligneId ? l : { ...l, ligneId: nouvelId() })));
  const [client, setClient] = useState(clientConnecte());
  const [authErreur, setAuthErreur] = useState(null);
  const [demande, setDemande] = useState(null);
  const clientIdRef = useRef(client?.id ? String(client.id) : null);


  // L'identité Shopify est persistée par PostHog : au retour OAuth et après un
  // rechargement, tous les événements et erreurs suivants sont attribués au client.
  useEffect(() => {
    if (!client?.id) return;

    const distinctId = String(client.id);
    if (clientIdRef.current && clientIdRef.current !== distinctId && posthog.__loaded) posthog.reset();

    // Ni e-mail ni nom chez PostHog : l'ID Shopify suffit à relier un parcours à un client.
    if (posthog.__loaded) posthog.identify(distinctId);
    clientIdRef.current = distinctId;
  }, [client]);

  // Retour OAuth : échange le code, stocke le profil, nettoie l'URL, et reprend
  // le parcours là où on l'a laissé (ex. soumission après connexion).
  useEffect(() => {
    if (!estCallback) return;
    handleCallback()
      .then((c) => {
        if (c) setClient(c);
        window.history.replaceState({}, '', '/');
        const brut = localStorage.getItem(POST_LOGIN_KEY);
        localStorage.removeItem(POST_LOGIN_KEY);
        let cible = { ecran: brut };
        try { if (brut && brut.startsWith('{')) cible = JSON.parse(brut); } catch { /* chaîne simple */ }
        if (cible.params) setParams(cible.params);
        setEcran(cible.ecran || 'accueil');
      })
      .catch((e) => setAuthErreur(e.message));
  }, [estCallback]);

  // Connexion automatique (SSO) en arrivant depuis rejouis.fr (page /pages/reprise,
  // bouton des fiches produit…) : si le client y est connecté, sa session Shopify
  // est ouverte → OAuth prompt=none, il revient connecté sans rien voir. Pas
  // connecté sur rejouis.fr → retour silencieux, il reste anonyme. Une seule
  // tentative par session de navigation (pas de boucle de redirections).
  // ?connexion=auto force la tentative (lien à mettre sur rejouis.fr si besoin).
  useEffect(() => {
    if (MODE_APERCU || estCallback || clientConnecte() || !authConfigure()) return;
    if (sessionStorage.getItem('reprise_sso_tente')) return;
    const chemin = window.location.pathname;
    if (/^\/(suivi|mes-demandes|retour)\/?$/.test(chemin)) return; // ces deep-links font déjà leur connexion
    let hote = '';
    try { hote = new URL(document.referrer).hostname; } catch { /* pas de referrer */ }
    const depuisRejouis = /(^|\.)rejouis\.fr$/i.test(hote) && hote !== window.location.hostname;
    const force = new URLSearchParams(window.location.search).get('connexion') === 'auto';
    if (!depuisRejouis && !force) return;
    sessionStorage.setItem('reprise_sso_tente', '1');
    // Où revenir après : le deep-link d'arrivée, sinon l'écran sauvegardé.
    const m = chemin.match(/^\/reprendre\/(.+)$/);
    const cible = m ? { ecran: 'reprendre', params: { handle: decodeURIComponent(m[1]) } }
      : /^\/prix[_-]reprise\/?$/.test(chemin) ? { ecran: 'prix-reprise' }
      : { ecran: sauve.ecran || 'accueil', params: sauve.params || null };
    localStorage.setItem(POST_LOGIN_KEY, JSON.stringify(cible));
    login({ silencieux: true }).catch(() => localStorage.removeItem(POST_LOGIN_KEY));
  }, []);

  // Deep-link /reprendre/<handle> (bouton « Je revends ce produit » des fiches
  // rejouis.fr) → écran de résolution, qui enchaîne sur le parcours produit.
  // On nettoie l'URL tout de suite : sinon un reload plus loin dans le parcours
  // (panier, vérifs…) renverrait l'utilisateur au début de ce produit.
  useEffect(() => {
    const m = window.location.pathname.match(/^\/reprendre\/(.+)$/);
    if (!m) return;
    setEcran('reprendre');
    setParams({ handle: decodeURIComponent(m[1]) });
    window.history.replaceState({}, '', '/');
  }, []);

  // Deep-link /suivi (lien « Mes demandes de reprise » du compte rejouis.fr) → suivi
  // des demandes. Pas connecté : on enchaîne directement sur l'OAuth (le client
  // vient de son compte rejouis.fr, sa connexion Shopify est déjà ouverte).
  useEffect(() => {
    if (!/^\/(suivi|mes-demandes)\/?$/.test(window.location.pathname)) return;
    // ?ref=REP-xxxx (lien des e-mails) : on se place sur cette demande.
    const ref = new URLSearchParams(window.location.search).get('ref');
    const p = ref && /^REP-\d+$/.test(ref) ? { ref } : null;
    window.history.replaceState({}, '', '/');
    setEcran('mes-demandes');
    setParams(p);
    if (!clientConnecte() && authConfigure()) {
      localStorage.setItem(POST_LOGIN_KEY, p ? JSON.stringify({ ecran: 'mes-demandes', params: p }) : 'mes-demandes');
      login().catch((e) => setAuthErreur(e.message));
    }
  }, []);

  // Deep-link /retour (lien « Retourner un article » du compte rejouis.fr), ?commande=<id>
  // pour présélectionner la commande. Même logique que /suivi : OAuth direct si pas connecté.
  useEffect(() => {
    if (!/^\/retour\/?$/.test(window.location.pathname)) return;
    const commande = new URLSearchParams(window.location.search).get('commande');
    const p = commande ? { commande } : null;
    window.history.replaceState({}, '', '/');
    setEcran('retour');
    setParams(p);
    if (!clientConnecte() && authConfigure()) {
      localStorage.setItem(POST_LOGIN_KEY, JSON.stringify({ ecran: 'retour', params: p }));
      login().catch((e) => setAuthErreur(e.message));
    }
  }, []);

  // Deep-link /prix_reprise → page publique « comment on calcule ton crédit ».
  useEffect(() => {
    if (/^\/prix[_-]reprise\/?$/.test(window.location.pathname)) setEcran('prix-reprise');
    // /offres : lien de la FAQ rejouis.fr (modèles boostés et repris en virement).
    if (/^\/offres\/?$/.test(window.location.pathname)) { setEcran('offres'); window.history.replaceState({}, '', '/'); }
    // /don : lien de l'e-mail « lot refusé ».
    if (/^\/don\/?$/.test(window.location.pathname)) setEcran('don');
  }, []);

  useEffect(() => {
    const ecranSauve = ECRANS_TRANSITOIRES.has(ecran) ? 'accueil' : ecran;
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({ ecran: ecranSauve, params, panier }));
    } catch {
      // Quota dépassé (photos du lot) : on garde au moins le panier sans les photos.
      try {
        const leger = panier.map((l) => (l.lot ? { ...l, photos: [], image: null } : l));
        localStorage.setItem(STORE_KEY, JSON.stringify({ ecran: ecranSauve, params, panier: leger }));
      } catch { /* stockage indisponible */ }
    }
  }, [ecran, params, panier]);

  // Chaque écran affiché : tracé (bloc « Parcours » du lab), porté par tous les
  // événements PostHog (propriété ecran) et mis dans l'URL (#panier…) pour que les
  // heatmaps et replays se séparent par écran. Les deep-links (déclarés plus haut)
  // ont déjà lu le chemin ; on ne touche qu'au hash.
  useEffect(() => {
    if (ecran === 'callback') return;
    suivre('screen_viewed', { screen: ecran });
    if (posthog.__loaded) posthog.register({ ecran });
    try {
      const h = `#${ecran}`;
      if (window.location.hash !== h) window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + h);
    } catch { /* URL non modifiable : sans conséquence */ }
  }, [ecran]);

  function go(e, p) { setEcran(e); setParams(p || null); }
  function onChoisir(produit) { go('etat', { handle: produit.handle, produit }); }
  function onSeConnecter() {
    if (authConfigure()) login().catch((e) => setAuthErreur(e.message));
    else go('compte'); // pas encore configuré → écran d'info
  }
  function onSuiviConnecter() {
    localStorage.setItem(POST_LOGIN_KEY, 'mes-demandes');
    onSeConnecter();
  }
  function onRetourConnecter() {
    localStorage.setItem(POST_LOGIN_KEY, JSON.stringify({ ecran: 'retour', params }));
    onSeConnecter();
  }
  function onDeconnecter() { if (posthog.__loaded) posthog.reset(); logout(); invaliderDemandes(); setClient(null); go('accueil'); }

  // ── Panier ──
  function onAjouter(ligne) {
    suivre('reprise_item_added', {
      item_type: ligne.lot ? 'lot' : 'catalogue_item',
      declared_condition: ligne.lot ? undefined : ligne.etatDeclare,
      estimated_credit: ligne.lot ? undefined : ligne.credit,
      wanted_boost_pct: ligne.boostPrio ? Math.round(ligne.boostPrio * 100) : undefined,
      lot_quantity: ligne.lot ? ligne.quantite : undefined,
    });
    // « Modifier l'état » remplace la ligne d'origine ; un lot remplace le lot (un seul par
    // demande) ; sinon c'est un jouet de plus, même s'il s'agit du même modèle.
    const { remplace, ...l } = ligne;
    const nouvelle = { ...l, ligneId: remplace || nouvelId() };
    setPanier((p) => [...p.filter((x) => (remplace ? x.ligneId !== remplace : !(ligne.lot && x.lot))), nouvelle]);
    go('panier');
  }
  function onRetirer(ligneId) { setPanier((p) => p.filter((l) => l.ligneId !== ligneId)); }
  function onModifier(ligne) {
    if (ligne.lot) { go('lot'); return; }
    go('etat', { handle: ligne.handle, produit: ligne.produit, remplace: ligne.ligneId });
  }

  // ── Vérifications → connexion → soumission ──
  function onContinuer() {
    if (client) { go('soumission'); return; }
    localStorage.setItem(POST_LOGIN_KEY, 'soumission');
    if (authConfigure()) login().catch((e) => setAuthErreur(e.message));
    else go('compte');
  }
  function onSoumissionSucces(res) { setDemande(res); setPanier([]); go('confirmation'); }

  let contenu;
  if (ecran === 'callback') {
    contenu = <CallbackEcran erreur={authErreur} onRetour={() => { window.history.replaceState({}, '', '/'); setEcran('accueil'); setAuthErreur(null); }} />;
  } else {
    switch (ecran) {
      case 'accueil': contenu = <Accueil go={go} panier={panier} client={client} onChoisir={onChoisir} />; break;
      case 'catalogue': contenu = <Catalogue go={go} panier={panier} params={params} onChoisir={onChoisir} />; break;
      case 'scan': contenu = <Scan go={go} panier={panier} onChoisir={onChoisir} />; break;
      case 'compte': contenu = <Compte go={go} client={client} onSeConnecter={onSeConnecter} onDeconnecter={onDeconnecter} />; break;
      case 'reprendre': contenu = <Reprendre go={go} handle={params?.handle} onChoisir={onChoisir} />; break;
      case 'etat': contenu = <Etat go={go} produit={params?.produit} remplace={params?.remplace} onAjouter={onAjouter} client={client} panier={panier} />; break;
      case 'panier': contenu = <Panier go={go} panier={panier} onRetirer={onRetirer} onModifier={onModifier} />; break;
      case 'verifications': contenu = <Verifications go={go} panier={panier} client={client} onContinuer={onContinuer} />; break;
      case 'soumission': contenu = <Soumission go={go} panier={panier} client={client} onSucces={onSoumissionSucces} />; break;
      case 'confirmation': contenu = <Confirmation go={go} demande={demande} />; break;
      case 'lot': contenu = <Lot go={go} panier={panier} onAjouter={onAjouter} />; break;
      case 'don': contenu = <Don go={go} />; break;
      case 'referencement': contenu = !REFERENCEMENT_ACTIF ? <Catalogue go={go} panier={panier} params={null} onChoisir={onChoisir} /> : <Reference go={go} client={client} params={params} panier={panier} onChoisir={onChoisir} onSeConnecter={onSeConnecter} />; break;
      case 'mes-demandes': contenu = <MesDemandes go={go} client={client} onSeConnecter={onSuiviConnecter} refOuverte={params?.ref} />; break;
      case 'retour': contenu = <Retour go={go} client={client} onSeConnecter={onRetourConnecter} commandeInitiale={params?.commande} />; break;
      case 'prix-reprise': contenu = <PrixReprise go={go} />; break;
      // « recherches » : ancien nom de l'écran (liens et sauvegardes existants).
      case 'recherches':
      case 'offres': contenu = <Recherches go={go} panier={panier} onChoisir={onChoisir} />; break;
      default: contenu = <Accueil go={go} panier={panier} client={client} onChoisir={onChoisir} />;
    }
  }

  // Largeur responsive : le catalogue s'étale (grille multi-colonnes sur desktop),
  // les écrans linéaires (état, panier, vérifs…) restent à une largeur de lecture confortable.
  const barreFixe = ecran === 'panier' || ecran === 'verifications'
    || (panier.length > 0 && ['accueil', 'catalogue', 'scan'].includes(ecran));
  return (
    <div className="proto-root" style={{ minHeight: '100vh', background: 'var(--blanc)' }}>
      <div style={{ maxWidth: LARGEUR_MAX, margin: '0 auto', minHeight: '100vh', position: 'relative', background: 'var(--blanc)' }}>
        {/* Retour boutique discret, en haut de tous les écrans (le pied de page en a un aussi) */}
        {ecran !== 'callback' && ecran !== 'soumission' ? (
          <div style={{ padding: '8px 18px 0' }}>
            <a href="https://rejouis.fr" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11.5, fontWeight: 500, color: 'var(--stone)', textDecoration: 'none' }}>
              <Icons.back size={13} /> Boutique rejouis.fr
            </a>
          </div>
        ) : null}
        {/* Logo cliquable → home, sur tous les écrans (la home a son propre en-tête) */}
        {ecran !== 'accueil' && ecran !== 'callback' && ecran !== 'soumission' ? (
          <div style={{ padding: '14px 18px 0' }}>
            <button onClick={() => go('accueil')} aria-label="Accueil" style={{ padding: 0, display: 'flex' }}><LogoRe size={18} /></button>
          </div>
        ) : null}
        {contenu}
        {ecran !== 'callback' ? <PiedBoutique barreFixe={barreFixe} onMesure={mesureConfiguree ? basculerMesure : null} /> : null}
        {MODE_APERCU ? null : <AvisPopup ecran={ecran} barreFixe={barreFixe} />}
      </div>
    </div>
  );
}

// Pied de page commun : retour vers la boutique rejouis.fr. Sous une barre fixe
// (panier, CTA des vérifications), on réserve sa hauteur pour rester cliquable.
function PiedBoutique({ barreFixe, onMesure }) {
  return (
    <footer style={{ padding: `20px 18px ${barreFixe ? 120 : 28}px`, textAlign: 'center', borderTop: '1px solid var(--bord)', marginTop: 8 }}>
      <a href="https://rejouis.fr" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--bleu)', textDecoration: 'none' }}>
        <Icons.store size={15} /> Retour à la boutique rejouis.fr
      </a>
      {onMesure ? (
        <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--stone)', lineHeight: 1.5 }}>
          {mesureRefusee() ? 'Mesure d’audience désactivée. ' : 'Ce site mesure son audience et enregistre les visites (saisies et photos masquées). '}
          <button onClick={onMesure} style={{ fontSize: 11.5, color: 'var(--stone)', textDecoration: 'underline', padding: 0 }}>
            {mesureRefusee() ? 'Réactiver' : 'Désactiver'}
          </button>
        </div>
      ) : null}
    </footer>
  );
}
